/**
 * ProductEditPage — /business/:slug/products/new  and /:slug/products/:productId/edit
 *
 * Owner/Manager/Editor form to create or update a product. Minimal MVP
 * fields (name, description, price, currency, image URL, category,
 * stock). Uses the business's slug to find its id via /businesses/by-slug.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Package, Loader2, ArrowLeft, Save, Trash2 } from "lucide-react";

export default function ProductEditPage() {
  const { slug, productId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const isNew = !productId;

  const [biz, setBiz] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", price: "", currency: "usd",
    image_url: "", category: "", stock: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [busy, setBusy]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: b } = await http.get(`/businesses/by-slug/${slug}`);
      setBiz(b);
      if (!isNew) {
        const { data: p } = await http.get(`/products/${productId}`);
        setForm({
          name: p.name || "", description: p.description || "",
          price: String(p.price ?? ""), currency: p.currency || "usd",
          image_url: p.image_url || "", category: p.category || "",
          stock: p.stock == null ? "" : String(p.stock),
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load.");
      nav(`/business/${slug}`);
    } finally { setLoading(false); }
  }, [slug, productId, isNew, nav]);

  useEffect(() => { load(); }, [load]);

  if (!user) return <div className="max-w-md mx-auto py-16 text-center"><Link to="/login" className="wofy-btn-primary">Sign in first</Link></div>;
  if (loading) return <div className="max-w-2xl mx-auto py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin opacity-60" /></div>;
  if (!biz) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Please enter the product name.");
    const priceNum = Number(form.price);
    if (!priceNum || priceNum < 0.5) return toast.error("Price must be at least 0.50.");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        price: priceNum,
        currency: form.currency,
        image_url: form.image_url || null,
        category: form.category || null,
        stock: form.stock === "" ? null : Number(form.stock),
      };
      if (isNew) {
        await http.post(`/businesses/${biz.id}/products`, payload);
        toast.success("Product created.");
      } else {
        await http.patch(`/products/${productId}`, payload);
        toast.success("Product saved.");
      }
      nav(`/business/${slug}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!confirm("Delete this product? This can't be undone.")) return;
    setBusy(true);
    try {
      await http.delete(`/products/${productId}`);
      toast.success("Product deleted.");
      nav(`/business/${slug}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="product-edit-page">
      <Link to={`/business/${slug}`} className="text-xs opacity-70 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Back to {biz.name}
      </Link>
      <h1 className="font-display text-3xl mb-1">{isNew ? "Add a product" : "Edit product"}</h1>
      <p className="opacity-70 text-sm mb-6">Shown on your business page and available to add to cart.</p>

      <div className="wofy-card !p-6 space-y-4">
        <Field label="Product name *"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="wofy-input" data-testid="pe-name" /></Field>
        <Field label="Description"><textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="wofy-input" data-testid="pe-description" placeholder="What is it, who is it for, what makes it special?" /></Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Price *"><input type="number" step="0.01" min="0.5" value={form.price} onChange={(e) => set("price", e.target.value)} className="wofy-input" data-testid="pe-price" placeholder="19.99" /></Field>
          <Field label="Currency">
            <select value={form.currency} onChange={(e) => set("currency", e.target.value)} className="wofy-input" data-testid="pe-currency">
              <option value="usd">USD</option><option value="eur">EUR</option>
              <option value="gbp">GBP</option><option value="inr">INR</option>
              <option value="aud">AUD</option><option value="cad">CAD</option>
            </select>
          </Field>
        </div>
        <Field label="Product image URL"><input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="wofy-input" data-testid="pe-image" placeholder="https://…" /></Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Category"><input value={form.category} onChange={(e) => set("category", e.target.value)} className="wofy-input" data-testid="pe-category" placeholder="apparel, food, digital…" /></Field>
          <Field label="Stock (leave blank for unlimited)"><input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} className="wofy-input" data-testid="pe-stock" /></Field>
        </div>

        <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: "var(--wofy-border)" }}>
          {!isNew ? (
            <button onClick={del} disabled={busy} className="text-xs text-red-600 inline-flex items-center gap-1 hover:underline" data-testid="pe-delete">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          ) : <span />}
          <button onClick={save} disabled={saving} className="wofy-btn-primary inline-flex items-center gap-2" data-testid="pe-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? "Create product" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">{label}</div>
      {children}
    </label>
  );
}
